// frontend/src/components/common/RefreshButton.jsx
import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * RefreshButton — the ONE refresh affordance.
 *
 * @description
 *   An icon-only button carrying the refresh symbol and a "Refresh" tooltip.
 *   `ariaLabel` is a REQUIRED prop and the component throws without it: an
 *   icon-only button has no visible name, and the repo already had three sites
 *   where a `title=` stood in for one — `title` is mouse-only and unreachable
 *   by keyboard (Regel 22 Nr. 8, Grill W9). While `loading` the button swaps
 *   its icon for a spinner and disables itself, so a second click cannot start
 *   a second fetch.
 *
 *   🔴 The spinner belongs to a RELOAD, never to the build-up. `firstLoad`
 *   says "this surface is still being built and shows skeletons" — there the
 *   button goes quiet: disabled, refresh glyph, no spinner. Two loading
 *   indicators for one fetch is the anti-pattern the skeletons exist to
 *   replace (`anti-patterns.md` Nr. 11, Skeletons only), and the spinner is
 *   decorative anyway (`aria-hidden`): the state is on the button, as
 *   `aria-busy`.
 *
 *   🔴 `firstLoad` means "this surface has NEVER loaded", NOT "the list is
 *   empty" (R16-Runde 1, 2026-10-07). A reload of a legitimately empty list — a
 *   month without FCA rows, a product without recommendations, a fresh
 *   knowledge base — is a reload, and there the spinner belongs. A caller that
 *   passes `length === 0` locks the button instead of letting it spin. The
 *   discriminator is a never-loaded expression (`data === null`,
 *   `!lastLoadedAt`, a `kpiLoading` flag), or a `loaded` flag where no such
 *   `null` exists.
 *
 * @useWhen
 *   - The only action of a page or a section is reloading the data it shows
 *   - A section heading needs its own reload next to the title
 *   - A card fetches its own data and needs its own refresh
 *   - The whole PAGE has exactly one action and it is a reload — then
 *     `ViewHeader`'s `iconOnly` slot renders this button (see `@dontUseFor`
 *     for the condition)
 *
 * @dontUseFor
 *   - A LABELLED retry in an empty or error surface — that stays a Button with
 *     the word "Retry"/"Try again", because the reader has to know what is
 *     being tried again (`ProductsTable.jsx`, `emptyState`). A symbol retry that
 *     already carries the refresh glyph IS a refresh and belongs here
 *     (`FulfillmentCostKpiCard.jsx`) — it just keeps its "Retry" tooltip
 *   - A labelled action that only LOOKS like a refresh because it spins the
 *     glyph while probing (`IntegrationCard.jsx` "Test connection") — that is
 *     a health check, not a reload
 *   - A primary action — a refresh is never primary and never `contained`
 *   - Anything that starts a job instead of reloading a view (use ProgressButton)
 *   - A page-header action slot as a LABELLED button — a refresh is never
 *     primary and never `contained`, and `ViewHeader`'s single unmarked action
 *     renders exactly that (E2).
 *     🔴 **Der `iconOnly`-Slot ist die EINE Ausnahme** (F3, UI-Durchgang 2
 *     Teil 3, 2026-10-08). `ViewHeader` mit `iconOnly: true` rendert DIESEN
 *     Baustein — als `IconButton` mit `aria-label`, nie `contained`; damit ist
 *     die ZWEITE Haelfte des alten Einwands weg (genau die, die E8 seinerzeit
 *     den Platz gekostet hat).
 *     🔴 **Die ERSTE Haelfte gilt weiter und ist die Bedingung:** ein
 *     Auffrischen gehoert dorthin, wo die DATEN liegen. Der Kopf-Slot ist
 *     deshalb nur richtig, wenn der Knopf die GANZE Seite neu laedt (eine
 *     Datenflaeche, ihr eigener Abruf — `HomeDashboard`, `InvoiceList`). Eine
 *     SEKTION mit eigenem Abruf behaelt ihren Knopf bei sich; dort waere der
 *     Kopf-Slot weiterhin falsch.
 *
 * @related
 *   - ProgressButton — kicks a long job and fills as it runs
 *   - Panel — the section this button usually sits in
 *
 * @reuseHints
 *   - Any table or panel with a fetch-on-mount effect
 *   - Dashboard cards that fetch once and want a manual reload
 *
 * @props
 *   - ariaLabel — REQUIRED accessible name; name WHAT is refreshed ("Refresh pricing data")
 *   - loading — a fetch is in flight: disables the button and sets `aria-busy`
 *   - firstLoad — the surface has NEVER loaded (skeletons on screen): disables
 *     the button and SUPPRESSES the spinner. NOT "the list is empty" — a reload
 *     of a legitimately empty list must spin
 *   - onClick — fired when neither `loading` nor `firstLoad`
 *   - tooltip — hover text, defaults to "Refresh"
 *   - size — MUI IconButton size, defaults to "small"
 *   - disabled — disables even when not loading
 */
export default function RefreshButton({
  ariaLabel,
  loading = false,
  firstLoad = false,
  onClick,
  tooltip = 'Refresh',
  size = 'small',
  disabled = false,
  ...iconButtonProps
}) {
  if (!ariaLabel) {
    // Same contract as `EmptyState`'s `reason`: a required prop that is missing
    // is a defect at the call site, not a state the component can render.
    throw new Error(
      'RefreshButton: `ariaLabel` is required — an icon-only button has no visible name.',
    );
  }

  const spinner = loading && !firstLoad;

  return (
    <Tooltip title={tooltip}>
      {/* 🔴 Der `span` ist kein Beiwerk. MUI warnt ohne ihn („You are providing
          a disabled `button` child to the Tooltip component … Add a simple
          wrapper element, such as a span") und der Tooltip haengt dann an einem
          Element, das keine Zeigerereignisse mehr annimmt — im deaktivierten
          Zustand also an gar nichts. Hausform wie
          `ActivityTimeline/ActivityEntryRow.jsx:560`. `display: inline-flex`
          haelt die Groesse des Knopfes, ein nackter `span` schoebe eine
          Grundlinien-Luecke in jede Zeile, in der er steht. */}
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton
          size={size}
          aria-label={ariaLabel}
          aria-busy={loading || undefined}
          onClick={loading || firstLoad ? undefined : onClick}
          disabled={disabled || loading || firstLoad}
          {...iconButtonProps}
        >
          {spinner
            ? (
              // 🔴 Dekorativ, `aria-hidden`: der Zustand steht am Knopf
              // (`aria-busy`), und ein namenloser `progressbar` in einem Knopf
              // ist fuer Screenreader Rauschen. Zweite, gemessene Wirkung:
              // `frontend/tests/rma/helpers.ts` wartet darauf, dass JEDER
              // sichtbare `[role="progressbar"]` ein `aria-valuenow` traegt —
              // ein indeterminierter Spinner hat keines. Die Sonde ueberspringt
              // `[aria-hidden="true"]` ausdruecklich, ein drehender Refresh
              // blockiert den Seitenaufbau damit nicht mehr.
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            )
            : <RefreshIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Tooltip>
  );
}
